var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
import { BdGraphNodeFactory, BdGraphFactory } from './graph.model';
export var GraphUtils;
(function (GraphUtils) {
    // get node data if it exists, otherwise return null
    function getNode(graph, id) {
        return graph.nodes[id] || null;
    }
    GraphUtils.getNode = getNode;
    // get nearest ancestor if it exists, otherwise return null
    // this is a (private) utility 
    function getAncestor(graph, id) {
        return graph.predecessors[id] || null;
    }
    GraphUtils.getAncestor = getAncestor;
    // union of all immediate ancestors of a set of nodes
    function getAllAncestorsFromList(graph, targets) {
        var ancestors = {};
        var _ancestor;
        targets.forEach(function (id) {
            _ancestor = getAncestor(graph, id);
            if (_ancestor && !ancestors[_ancestor]) {
                ancestors[_ancestor] = true;
            }
        });
        return Object.keys(ancestors);
    }
    GraphUtils.getAllAncestorsFromList = getAllAncestorsFromList;
    // return all child node ids of target node
    function getSuccessors(graph, id) {
        return __spread(graph.successors[id] || []);
    }
    GraphUtils.getSuccessors = getSuccessors;
    // get graph node state matching properties
    function getState(graph, id, properties) {
        var state = {};
        var node = getNode(graph, id);
        Object.entries(node.boolState).forEach(function (_a) {
            var _b = __read(_a, 2), property = _b[0], value = _b[1];
            if (properties && properties.includes(property)) {
                state[property] = value;
            }
        });
        Object.entries(node.numState).forEach(function (_a) {
            var _b = __read(_a, 2), property = _b[0], value = _b[1];
            if (properties && properties.includes(property)) {
                state[property] = value;
            }
        });
        return state;
    }
    GraphUtils.getState = getState;
    // return true if node is a leaf node
    function isLeaf(graph, id) {
        return !!graph.nodes[id] && graph.successors[id] === undefined;
    }
    GraphUtils.isLeaf = isLeaf;
    // return true if node is a root node
    function isRoot(graph, id) {
        return graph.roots.hasOwnProperty(id) || false;
    }
    GraphUtils.isRoot = isRoot;
    // return all root-node ids
    function getRoots(graph) {
        return Object.keys(graph.roots);
    }
    GraphUtils.getRoots = getRoots;
    // recursively build graphNode from nested element
    function scrapeDataNode(graph, parentId, nodeData, idKey, childKey, props, depth) {
        // create graph node as specified generic type node
        // const graphNode = new BdGraphNode(nodeData[idKey]) as TNode;
        var graphNode = BdGraphNodeFactory(nodeData[idKey]);
        // set Depth
        graphNode.depth = depth;
        // collect properties 
        Object.entries(props).forEach(function (_a) {
            var _b = __read(_a, 2), k = _b[0], v = _b[1];
            var val;
            // get value (from string or function)
            if (typeof v === 'string') {
                val = nodeData[v];
            }
            else if (typeof v === 'function') {
                val = v(nodeData[idKey]);
            }
            // store as boolean or numerical state property
            if (typeof val === 'boolean') {
                graphNode.boolState[k] = val;
            }
            else if (typeof val === 'number') {
                graphNode.numState[k] = val;
            }
            else {
                graphNode[k] = val;
            }
        });
        // add node and relationships to graph
        graph.nodes[graphNode.id] = graphNode;
        if (parentId) {
            graph.successors[parentId] = __spread(graph.successors[parentId] || [], [graphNode.id]);
            graph.predecessors[graphNode.id] = parentId;
        }
        else {
            graph.roots[graphNode.id] = graphNode;
        }
        // recurse 
        depth += 1;
        nodeData[childKey].forEach(function (childData) {
            scrapeDataNode(graph, graphNode.id, childData, idKey, childKey, props, depth);
        });
    }
    // depth first traversal and do
    // this is a (private) utility 
    function depthFirst(graph, doFunc, id) {
        if (!id) {
            // start at the top fo the graph and operate on all roots
            getRoots(graph).forEach(function (rid) {
                doFunc(rid);
                depthFirst(graph, doFunc, rid);
            });
        }
        else {
            // act on node and keep going down
            doFunc(id);
            getSuccessors(graph, id).forEach(function (cid) {
                depthFirst(graph, doFunc, cid);
            });
        }
    }
    GraphUtils.depthFirst = depthFirst;
    // factory for new BdGraph nested objects 
    var DEFAULT_ID_KEY = 'id';
    var DEFAULT_CHILDREN_KEY = 'children';
    function createGraphFromSrc(srcData, idKey, childKey, nodeProperties) {
        if (idKey === void 0) { idKey = DEFAULT_ID_KEY; }
        if (childKey === void 0) { childKey = DEFAULT_CHILDREN_KEY; }
        if (nodeProperties === void 0) { nodeProperties = {}; }
        var depth = 0; // depth of root nodes is 0
        var graph = BdGraphFactory();
        srcData.forEach(function (nodeData) {
            scrapeDataNode(graph, null, nodeData, idKey, childKey, nodeProperties, depth);
        });
        return graph;
    }
    GraphUtils.createGraphFromSrc = createGraphFromSrc;
    function pruneHiddenObjects(sceneGraph, showObjects, ancestorShowObjectValue) {
        var e_1, _a;
        var filteredSceneGraph = [];
        var _loop_1 = function (node) {
            var showValue = showObjects[node.objectId];
            // use show objects to see whether or not we preserve this node
            // when show objects for this node is undefined, fall back to the ancestor's show object value
            var shouldPreserveNode = (function () {
                if (showValue === true) {
                    return true;
                }
                if (showValue === false) {
                    return false;
                }
                return ancestorShowObjectValue;
            })();
            // recurse for all children
            var filteredChildren = pruneHiddenObjects(node.objects || [], showObjects, shouldPreserveNode);
            if (shouldPreserveNode) {
                // preserve the node and its children, if any
                var nodeCopy = __assign({}, node);
                nodeCopy.objects = filteredChildren;
                filteredSceneGraph.push(nodeCopy);
            }
            else {
                // throw out the node, and only save its children, if any
                filteredChildren.forEach(function (filtered) { return filteredSceneGraph.push(filtered); });
            }
        };
        try {
            for (var sceneGraph_1 = __values(sceneGraph), sceneGraph_1_1 = sceneGraph_1.next(); !sceneGraph_1_1.done; sceneGraph_1_1 = sceneGraph_1.next()) {
                var node = sceneGraph_1_1.value;
                _loop_1(node);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (sceneGraph_1_1 && !sceneGraph_1_1.done && (_a = sceneGraph_1.return)) _a.call(sceneGraph_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return filteredSceneGraph;
    }
    GraphUtils.pruneHiddenObjects = pruneHiddenObjects;
})(GraphUtils || (GraphUtils = {}));
